<template>
  <div v-if="showMessageDate(visibleMessages, index)" class="message-date">
    {{ getMessageDate(message) }}
  </div>
  <div
    v-if="!message.hidden"
    :id="messageElementId"
    class="message-wrapper"
    :class="{
      isBlocking: isBlockingTextShown && index === visibleMessages.length - 1,
      isActive: isControlsActive,
    }"
  >
    <div class="message" :class="getIsMyMessageClass(message, authId)">
      <div class="message-content-wrapper">
        <div class="message-content-wrapper-inner" :class="{ isMine }">
          <MessageExtrasWrapper
            v-if="!getIsAutomaticMessageType(message) && message.replyToMessage"
            :title="getMessageReplyTitle({ message, authId, isMine, t })"
            :is-mine="isMine"
            :icon="IconType.REPLY"
          >
            <div v-if="!message.isForwarded" class="message-reply-bottom">
              <div class="reply-text-wrapper" @click="fetchPreviousMessages">
                <span
                  v-if="textMessagePreviewText"
                  class="reply-text"
                  :class="{ isDeleted: message.replyToMessage.isDeleted }"
                  >{{ textMessagePreviewText }}</span
                >
                <ImageAttachment
                  v-if="
                    getIsMessageTextType(message.replyToMessage) &&
                    message.replyToMessage?.imageAttachments?.length
                  "
                  class="replyImageAttachment"
                  :class="{ hasNoText: !textMessagePreviewText }"
                  :attachment="message.replyToMessage.imageAttachments[0]"
                  :message="message"
                  :is-shrinked="true"
                />
              </div>
            </div>
          </MessageExtrasWrapper>
          <MessageExtrasWrapper
            v-if="
              getIsMessageTextType(message) &&
              message.isModified &&
              !message.isForwarded
            "
            :title="t('chat.chatContainer.messages.editing.modified')"
          />
          <MessageExtrasWrapper
            v-if="!getIsAutomaticMessageType(message) && message.isForwarded"
            :title="forwardedMessageTitle"
            :icon="IconType.FORWARD_MESSAGE"
          />
          <ChatMessageReactions
            :message="message"
            :is-active="isActive"
            :has-shadow="hasShadow"
            :is-modal-open="isReactionsModalOpen"
            :modal-id="reactionsModalId"
            :element-id="messageContentElementId"
            :position="positionObject"
            :is-touch-end-enabled="isTouchEndEnabled"
            @hide-shadow="hideShadow"
            @open-modal="openReactionsModal"
          >
            <div
              v-if="isMessageContentShown"
              class="message-content"
              :class="[getMessageClass(visibleMessages, index, authId)]"
              v-on="handlers"
            >
              <slot />
            </div>
            <template
              v-if="!getIsAutomaticMessageType(message) && !message.isDeleted"
              #checkboxSelectMessage
            >
              <CheckboxSelectMessage :message="message" />
            </template>
          </ChatMessageReactions>
        </div>
        <slot name="imageAttachment" />
        <slot name="videoPlayer" />
      </div>
    </div>
  </div>
  <BackdropModal :enabled="isReactionsModalOpen">
    <ChatMessageReactionsModal
      v-if="isReactionsModalOpen && !getIsAutomaticMessageType(message)"
      :id="reactionsModalId"
      :message="message"
      @close-reactions-modal="closeReactionsModal"
    />
  </BackdropModal>
</template>

<script setup lang="ts">
import ChatMessageReactions from "../ChatMessageReactions/ChatMessageReactions.vue";
import { IconType } from "@/types/icons";
import {
  showMessageDate,
  getMessageClass,
  getMessageDate,
  getIsMessageTextType,
  getIsAutomaticMessageType,
  getMessageReplyTitle,
  getMessageContentElementId,
  skipToReplyMessage,
  getIsMessageAudioType,
} from "@/utils/message";
import { getIsMyMessageClass } from "@/utils/chat";
import {
  ChatState,
  Message,
  NonAutomaticMessage,
  Positions,
  TextMessage,
} from "@/store/chats/types";
import { useComputedValue, useLongPress } from "@/composables";
import { GetterTypes, MutationTypes } from "@/store";
import { computed, nextTick, onUnmounted, reactive, ref } from "vue";
import { getIsBlockingTextShown } from "@/utils/chat";
import { useStore } from "vuex";
import { ChatsMutationTypes } from "@/store/chats";
import { CustomClickEvent } from "@/types/app";
import { findParentNodeById, getSafeElementPosition } from "@/utils/app";
import ChatMessageReactionsModal from "../ChatMessageReactionsModal/ChatMessageReactionsModal.vue";
import BackdropModal from "@/components/modals/BackdropModal/BackdropModal.vue";
import { LayoutTypes } from "@/store/app/state";
import { useI18n } from "vue-i18n";
import { getRestrictedMessagesRef } from "@/firebase/ref";
import firebase from "@/firebase/firebase";
import ImageAttachment from "../ImageAttachment/ImageAttachment.vue";
import CheckboxSelectMessage from "@/components/checkbox/CheckboxSelectMessage.vue";
import MessageExtrasWrapper from "../MessageExtrasWrapper/MessageExtrasWrapper.vue";

interface ChatMessageContentProps {
  message: Message;
  index: number;
  visibleMessages: Message[];
}

interface Emits {
  (e: "replyToMessage", message: NonAutomaticMessage): void;
  (e: "modifyMessage", message: TextMessage): void;
}

const emit = defineEmits<Emits>();

const { commit } = useStore();
const props = defineProps<ChatMessageContentProps>();
const isBlockingTextShown = getIsBlockingTextShown();
const authId = useComputedValue<number>(GetterTypes.GET_AUTH_ID);
const messageReactionId = useComputedValue<string | null>(
  GetterTypes.GET_MESSAGE_REACTION_ID,
);
const { t } = useI18n();
const positionObject = reactive<Positions>({
  top: 0,
  left: 0,
  width: 0,
  height: undefined,
});
const selectedMessageIds = useComputedValue<string[] | null>(
  GetterTypes.GET_SELECTED_MESSAGE_IDS,
);
const messageElementId = computed(() => `message_${props.message.id}`);
const messageContentElementId = computed(() =>
  getMessageContentElementId(props.message.id),
);
const hasShadow = ref(false);
const isReactionsModalOpen = ref(false);
const isMessageAutomatic = computed(() =>
  getIsAutomaticMessageType(props.message),
);

const textMessagePreviewText = computed(() => {
  if (getIsAutomaticMessageType(props.message)) {
    return;
  }
  const { replyToMessage } = props.message;
  if (!replyToMessage) {
    return;
  }

  if (
    getIsMessageTextType(replyToMessage) &&
    replyToMessage?.imageAttachments?.length &&
    !replyToMessage.messageText
  ) {
    return;
  }

  if (getIsMessageTextType(replyToMessage) && replyToMessage.isDeleted) {
    return t("chat.chatContainer.messages.deleting.deletedMessage");
  }
  if (getIsMessageTextType(replyToMessage)) {
    return replyToMessage.displayMessageText;
  }

  if (getIsMessageAudioType(replyToMessage) && replyToMessage.isDeleted) {
    return t("chat.chatContainer.messages.deleting.deletedAudioMessage");
  }

  return t("chat.chatContainer.messages.audioMessage");
});

const isActive = computed(
  () =>
    !isMessageAutomatic.value && messageReactionId.value === props.message.id,
);
const isControlsActive = computed(
  () => isActive.value && !isReactionsModalOpen.value,
);
const reactionsModalId = computed(() => `reactions-modal-${props.message.id}`);
const layoutType = useComputedValue<LayoutTypes>(GetterTypes.GET_LAYOUT_TYPE);
const isMobile = computed(() => layoutType.value === LayoutTypes.MOBILE);
const isTouchEndEnabled = ref(false);
const isMine = computed(() => props.message.senderId === authId.value);
const chat = useComputedValue<ChatState | undefined>(
  GetterTypes.GET_SELECTED_CHAT_STATE,
);
const firstMessage = computed(() => {
  const firstMessageId = chat.value?.messageIds[0];
  if (!firstMessageId) {
    return null;
  }
  return chat.value.messages[firstMessageId];
});
const isReactingEnabled = computed(
  () => !getIsAutomaticMessageType(props.message) && !props.message.isDeleted,
);

const isMessageContentShown = computed<boolean>(() => {
  const { message } = props;
  if (!getIsMessageTextType(message)) {
    return true;
  }

  const { messageText, othersAttachments, imageAttachments, isDeleted } =
    message;

  return Boolean(
    messageText ||
      othersAttachments ||
      imageAttachments?.length === 1 ||
      isDeleted,
  );
});

const handlePosition = () => {
  const messageContentEl = document.getElementById(
    messageContentElementId.value,
  );
  if (!messageContentEl) {
    return;
  }

  const { top, left, width, height } = getSafeElementPosition(messageContentEl);

  positionObject.left = left;
  positionObject.top = top;
  positionObject.width = width;
  positionObject.height = height;
  hasShadow.value = true;
};

const onLongPress = (event: CustomClickEvent) => {
  // if the user is in deleting messages mode, or if the message is automatic, or the message is deleted, reactions are not allowed
  if (
    selectedMessageIds.value ||
    isMessageAutomatic.value ||
    !isMobile.value ||
    !isReactingEnabled.value
  ) {
    return;
  }
  const target = event.target;
  commit(ChatsMutationTypes.SET_MESSAGE_REACTION_ID, {
    messageId: props.message.id,
  });

  const wrapper = findParentNodeById(target, messageElementId.value);
  if (!wrapper) {
    return;
  }

  disableScroll();
  handlePosition();
  disableTouchEnd();
};

const handleEnableTouchEnd = () => {
  isTouchEndEnabled.value = true;
};

const disableTouchEnd = () => {
  isTouchEndEnabled.value = false;
  window.addEventListener("touchend", handleEnableTouchEnd);
  window.addEventListener("mouseup", handleEnableTouchEnd);
};

const clearDisableTouchEnd = () => {
  isTouchEndEnabled.value = true;
  window.removeEventListener("touchend", handleEnableTouchEnd);
  window.removeEventListener("mouseup", handleEnableTouchEnd);
};

onUnmounted(() => {
  clearDisableTouchEnd();
  closeReactionsModal();
});

const disableScroll = () => {
  document.body.style.overflow = "hidden";
};

const hideShadow = () => {
  positionObject.left = 0;
  positionObject.top = 0;
  hasShadow.value = false;
  clearDisableTouchEnd();
};

const handlers = useLongPress({ onLongPress });

const openReactionsModal = () => {
  commit(ChatsMutationTypes.SET_MESSAGE_REACTION_ID, {
    messageId: props.message.id,
  });
  isReactionsModalOpen.value = true;
};

const closeReactionsModal = () => {
  commit(ChatsMutationTypes.SET_MESSAGE_REACTION_ID, {
    messageId: null,
  });
  isReactionsModalOpen.value = false;
};

const forwardedMessageTitle = computed(() => {
  if (isMine.value) {
    return t("chat.chatContainer.messages.forwarded.ownMessage");
  }
  return t("chat.chatContainer.messages.forwarded.message");
});

const fetchPreviousMessages = async () => {
  const message = props.message;
  if (getIsAutomaticMessageType(message) || !message.replyToMessage) {
    return;
  }

  if (!chat.value) {
    return;
  }

  const { chatId, messageIds } = chat.value;

  if (messageIds.includes(message.replyToMessage.id)) {
    skipToReplyMessage(message.replyToMessage);
    return;
  }

  if (!firstMessage.value) {
    return;
  }

  const messageSentAt = firstMessage.value.sentAt;
  const { sentAt } = message.replyToMessage;
  const replyToMessageSentAt = firebase.firestore.Timestamp.fromMillis(sentAt);
  const restrictedMessageRef = getRestrictedMessagesRef(
    chatId,
    messageSentAt,
  ).where("sentAt", ">=", replyToMessageSentAt);
  const messagesRef = await restrictedMessageRef.get();
  const messages = messagesRef.docs.map((mess) => mess.data());

  if (messages.length === 0) {
    return;
  }
  commit(MutationTypes.PREPEND_MESSAGES, { messages, chatId });
  await nextTick();
  skipToReplyMessage(message.replyToMessage);
};
</script>

<style lang="scss">
@import "./ChatMessageContent.scss";
</style>
